/**
 * Password Validation Module
 * Provides functions to validate passwords according to security requirements.
 */

/**
 * Password policy requirements:
 * 1. Minimum 8 characters in length
 * 2. Contains at least one uppercase letter
 * 3. Contains at least one lowercase letter
 * 4. Contains at least one number
 * 5. Contains at least one special character
 */

/**
 * Checks if a string contains at least one letter (a-z, A-Z)
 */
function containsLetter(password: string): boolean {
  return /[a-zA-Z]/.test(password);
}

/**
 * Checks if a string contains at least one number (0-9)
 */
function containsNumber(password: string): boolean {
  return /[0-9]/.test(password);
}

/**
 * Checks if a string contains at least one uppercase letter (A-Z)
 */
function containsUppercase(password: string): boolean {
  return /[A-Z]/.test(password);
}

/**
 * Checks if a string contains at least one lowercase letter (a-z)
 */
function containsLowercase(password: string): boolean {
  return /[a-z]/.test(password);
}

/**
 * Checks if a string contains at least one special character
 */
function containsSpecialCharacter(password: string): boolean {
  const specialChars = '!"#$%&\'()*+,-./:;<=>?@[\\]^_{|}~';
  return [...password].some(char => specialChars.includes(char));
}

/**
 * Validates if a password meets all security requirements
 * 
 * @param password - The password to validate
 * @returns True if the password meets all requirements, false otherwise
 */
function validatePassword(password: string): boolean {
  // Check if password exists
  if (!password) {
    return false;
  }

  // Check minimum length requirement
  if (password.length < 8) {
    return false;
  }

  // Check all character type requirements
  const hasRequiredCharacters =
    containsNumber(password) &&
    containsLetter(password) &&
    containsUppercase(password) &&
    containsLowercase(password) &&
    containsSpecialCharacter(password);

  return hasRequiredCharacters;
}

/**
 * Public API function for password validation
 * 
 * @param password - The password to validate
 * @returns True if the password meets all requirements, false otherwise
 */
export function passwordPattern(password: string): boolean {
  return validatePassword(password);
}